import { useEffect, useState } from "preact/hooks"
import { api } from "../lib/api"
import type { BoxDetail, Role } from "../lib/types"
import { useConfirm } from "./Confirm"
import { Spinner } from "./Spinner"

interface MembersViewProps {
	boxId: number
}

// Members have no avatar (auth is email/password, not Google) — show
// initials in a small badge instead, same convention as Navbar.
function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return "?"
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[1][0]).toUpperCase()
}

// The invite textarea accepts a jumble of addresses — commas, semicolons,
// spaces, newlines all count as separators.
function parseEmails(raw: string): string[] {
	const parts = raw
		.split(/[\s,;]+/)
		.map((s) => s.trim().toLowerCase())
		.filter((s) => s.includes("@"))
	return [...new Set(parts)]
}

export function MembersView({ boxId }: MembersViewProps) {
	const confirm = useConfirm()
	const [detail, setDetail] = useState<BoxDetail | null>(null)
	const [error, setError] = useState("")
	const [showInvite, setShowInvite] = useState(false)
	const [emails, setEmails] = useState("")
	const [inviting, setInviting] = useState(false)
	const [busyKey, setBusyKey] = useState("")

	const load = () => {
		api
			.getBox(boxId)
			.then(setDetail)
			.catch((err) =>
				setError(err instanceof Error ? err.message : "Failed to load members"),
			)
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: `load` is stable enough for this component's lifetime
	useEffect(load, [boxId])

	if (error && !detail) {
		return (
			<div class="container py-4">
				<div class="alert alert-danger">{error}</div>
			</div>
		)
	}
	if (!detail) {
		return (
			<div class="container py-4">
				<Spinner />
			</div>
		)
	}

	// Viewers can technically navigate here directly; the server only sends
	// members/invites to owners, so bail out with a friendly message instead
	// of rendering an empty management screen.
	if (detail.role !== "owner") {
		return (
			<div class="container py-4">
				<div class="alert alert-warning">
					Only box owners can manage members.
				</div>
				<a href={`/boxes/${boxId}`} class="btn btn-outline-secondary">
					Back to box
				</a>
			</div>
		)
	}

	// The backend invites one email per call, so fan out sequentially and
	// collect per-address failures to report at the end.
	const inviteAs = async (role: Role) => {
		const targets = parseEmails(emails)
		if (targets.length === 0) return
		setInviting(true)
		setError("")
		const failures: string[] = []
		for (const target of targets) {
			try {
				await api.inviteMember(boxId, target, role)
			} catch (err) {
				failures.push(
					`${target}: ${err instanceof Error ? err.message : "failed"}`,
				)
			}
		}
		setInviting(false)
		if (failures.length > 0) {
			setError(`Some invites failed: ${failures.join("; ")}`)
		} else {
			setEmails("")
			setShowInvite(false)
		}
		load()
	}

	const removeMember = async (userId: number) => {
		if (!(await confirm("Remove this member from the box?"))) return
		setBusyKey(`member-${userId}`)
		setError("")
		try {
			await api.removeMember(boxId, userId)
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to remove member")
		} finally {
			setBusyKey("")
		}
	}

	const revokeInvite = async (inviteId: number) => {
		setBusyKey(`invite-${inviteId}`)
		setError("")
		try {
			await api.revokeInvite(boxId, inviteId)
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to revoke invite")
		} finally {
			setBusyKey("")
		}
	}

	const members = detail.members ?? []
	const invites = detail.invites ?? []
	const canInvite = parseEmails(emails).length > 0 && !inviting

	return (
		<div class="container py-3 py-sm-4" style={{ maxWidth: "640px" }}>
			<div class="d-flex align-items-center justify-content-between mb-3 gap-2">
				<h1 class="h4 mb-0 text-truncate">Members: {detail.box.name}</h1>
				<div class="d-flex gap-2 flex-shrink-0">
					<button
						type="button"
						class="btn btn-primary"
						onClick={() => setShowInvite(true)}
					>
						<i class="bi bi-envelope-plus" aria-hidden="true" />
						<span>Invite</span>
					</button>
					<a
						href={`/boxes/${boxId}`}
						class="btn btn-outline-secondary flex-shrink-0"
					>
						<i class="bi bi-arrow-left" aria-hidden="true" />
						<span>Back</span>
					</a>
				</div>
			</div>

			{showInvite && (
				<>
					<div class="modal fade show d-block" role="dialog" aria-modal="true">
						<div class="modal-dialog">
							<div class="modal-content">
								<div class="modal-header">
									<h2 class="modal-title fs-5">Invite to {detail.box.name}</h2>
									<button
										type="button"
										class="btn-close"
										aria-label="Close"
										onClick={() => setShowInvite(false)}
									/>
								</div>
								<div class="modal-body">
									<label class="form-label" for="invite-emails">
										Email addresses
									</label>
									<textarea
										id="invite-emails"
										class="form-control mb-3"
										rows={3}
										placeholder={
											"grandma@example.com, grandpa@example.com\nuncle@example.com"
										}
										value={emails}
										onInput={(e) =>
											setEmails((e.target as HTMLTextAreaElement).value)
										}
										autoFocus
									/>
									<div class="row g-3">
										<div class="col-sm-6 d-grid gap-1 align-content-start">
											<button
												type="button"
												class="btn btn-primary"
												disabled={!canInvite}
												onClick={() => inviteAs("viewer")}
											>
												{inviting ? (
													<Spinner size={16} />
												) : (
													<i class="bi bi-eye" aria-hidden="true" />
												)}
												<span>Invite as viewer</span>
											</button>
											<p class="small text-body-secondary text-center mb-0">
												Can look, but cannot add art.
											</p>
										</div>
										<div class="col-sm-6 d-grid gap-1 align-content-start">
											<button
												type="button"
												class="btn btn-primary"
												disabled={!canInvite}
												onClick={() => inviteAs("owner")}
											>
												{inviting ? (
													<Spinner size={16} />
												) : (
													<i class="bi bi-pencil" aria-hidden="true" />
												)}
												<span>Invite as owner</span>
											</button>
											<p class="small text-body-secondary text-center mb-0">
												Can add art and manage members.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						class="modal-backdrop fade show"
						onClick={() => setShowInvite(false)}
					/>
				</>
			)}

			{error && <div class="alert alert-danger">{error}</div>}

			<ul class="list-unstyled mb-0">
				{members.map((m) => (
					<li
						class="d-flex align-items-center justify-content-between gap-2 py-2"
						key={`member-${m.user_id}`}
					>
						<div class="d-flex align-items-center gap-2 text-truncate">
							<span
								class="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary-emphasis fw-semibold flex-shrink-0"
								style={{ width: "28px", height: "28px", fontSize: "0.7rem" }}
								aria-hidden="true"
							>
								{initials(m.name)}
							</span>
							<div class="text-truncate">
								<div class="text-truncate">{m.name}</div>
								<div class="small text-body-secondary text-truncate">
									{m.email}
								</div>
							</div>
						</div>
						<div class="d-flex align-items-center gap-2 flex-shrink-0">
							<span class="badge text-bg-accent">{m.role}</span>
							<button
								type="button"
								class="btn btn-sm btn-outline-danger"
								onClick={() => removeMember(m.user_id)}
								disabled={busyKey === `member-${m.user_id}`}
								aria-label="Remove member"
							>
								{busyKey === `member-${m.user_id}` ? (
									<Spinner size={16} />
								) : (
									<i class="bi bi-x-lg" aria-hidden="true" />
								)}
							</button>
						</div>
					</li>
				))}
				{invites.map((inv) => (
					<li
						class="d-flex align-items-center justify-content-between gap-2 py-2"
						key={`invite-${inv.id}`}
					>
						<div class="text-truncate">
							<div class="text-truncate">{inv.email}</div>
							<div class="small text-body-secondary text-truncate">
								Invited as {inv.role}
							</div>
						</div>
						<div class="d-flex align-items-center gap-2 flex-shrink-0">
							<span class="badge text-bg-secondary">pending</span>
							<button
								type="button"
								class="btn btn-sm btn-outline-danger"
								onClick={() => revokeInvite(inv.id)}
								disabled={busyKey === `invite-${inv.id}`}
								aria-label="Revoke invite"
							>
								{busyKey === `invite-${inv.id}` ? (
									<Spinner size={16} />
								) : (
									<i class="bi bi-x-lg" aria-hidden="true" />
								)}
							</button>
						</div>
					</li>
				))}
				{members.length + invites.length === 0 && (
					<li class="text-body-secondary py-2">No members yet.</li>
				)}
			</ul>
		</div>
	)
}
